// options-io.ts — back the DOM menu's MenuOptionsIO with the SAME OptionsState +
// sndm + SavePreferences round-trip the canvas InGameOptionsForm uses (HTML/DOM
// UI shell plan, Phase 2). Pure (no DOM), node-testable, and shared by main.ts
// AND the tests so both exercise the IDENTICAL difficulty mapping — no drift
// (the alternative, each re-declaring the mapping, was flagged as fragile).

import { SavePreferences, kcmsUpdate } from '../io/prefs.ts';
import type { PrefStore, Preferences } from '../io/prefs.ts';
import type { OptionsState } from '../ui/shellform.ts';
import { kfHcapEasy, kfHcapNormal, kfHcapHard } from '../../../sim/src/player/side.ts';
import { gatGameSpeeds, GetSpeedMultiplierString } from '../ui/gameoptions.ts';
import type { MenuOptionsIO, Difficulty } from './menu-controller.ts';

// the SAME flags the canvas InGameOptionsForm sets (gameoptions.ts Easy/Normal/Hard)
export const difficultyToHcap = (d: Difficulty): number =>
  d === 'easy' ? kfHcapEasy : d === 'hard' ? kfHcapHard : kfHcapNormal;
export const hcapToDifficulty = (wf: number): Difficulty =>
  wf === kfHcapEasy ? 'easy' : wf === kfHcapHard ? 'hard' : 'normal';

// the game-speed slider helpers, SHARED by the shell (this file) AND the in-game options
// (game-options-io.ts) so the two paths can't drift — the in-game path imports these.
/** the index of the table entry CLOSEST to `t` — NOT exact indexOf: game speeds like the
 *  11x notch (kt11x = 8/11) are fractions, so float drift in the live speed would miss an
 *  exact match and snap the slider thumb to a wrong default while the readout shows the
 *  real speed (the thumb/value mismatch this replaced). */
export const closestNotch = (tbl: readonly number[], t: number): number => {
  let best = 0,
    bestD = Infinity;
  for (let i = 0; i < tbl.length; i++) {
    const d = Math.abs(tbl[i] - t);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
};
/** format a game/scroll speed as "W.FFx" (trunc whole + a 2-digit truncated fraction). */
export const formatSpeedX = (v: number): string => {
  const w = Math.trunc(v);
  const f = Math.trunc((v - w) * 100);
  return `${w}.${f}x`;
};

/** What the options seam reads/writes — the canvas shell's globals (or, in tests,
 *  stand-ins built the same way: OptionsState + a SoundMgr-shaped object + prefs). */
export interface MenuOptionsBacking {
  options: OptionsState;
  sndm: { GetVolume(): number; IsEnabled(): boolean; Enable(f: boolean): void };
  prefs: Preferences;
  prefStore: PrefStore;
  /** gtGameSpeed, read at save time. */
  gameSpeed: () => number;
}

/** Build the MenuOptionsIO. At the menu there is no plrLocal, so difficulty is
 *  applied to OptionsState only (a launched game picks it up via gameSessionOpts);
 *  a future in-game options screen (Phase 5) must also call plrLocal.SetHandicap. */
export function makeMenuOptionsIO(b: MenuOptionsBacking): MenuOptionsIO {
  // the game-speed notch (a gatGameSpeeds entry) is held locally until OK persists
  // it — the menu has no live game to SetGameSpeed on (game/GameOptions.cpp:432).
  let tGameSpeed = b.gameSpeed();
  // The shell options is the 15-notch table (gatGameSpeeds) — the 11x SP notch (kt11x = 8/11)
  // is an in-game-only speed NOT in this table. Use the shared closestNotch (not exact indexOf,
  // which fell back to mid-track while the readout showed the raw 11.0x — the thumb/value
  // mismatch). Snap the OPENING speed to a representable shell notch so the thumb, the label,
  // AND a subsequent save all agree (a game left at 11x snaps to the nearest shell speed;
  // Cancel leaves the live gtGameSpeed untouched — this local tGameSpeed only persists on save).
  tGameSpeed = gatGameSpeeds[closestNotch(gatGameSpeeds, tGameSpeed)];
  const notch = (): number => closestNotch(gatGameSpeeds, tGameSpeed);
  const clampScroll = (v: number): number =>
    Math.max(1, Math.min(5, Math.round((v - 1) / 0.25) * 0.25 + 1));
  return {
    difficulty: () => hcapToDifficulty(b.options.wfHandicap),
    setDifficulty: (d) => {
      b.options.wfHandicap = difficultyToHcap(d);
    },
    muted: () => !b.sndm.IsEnabled(),
    setMuted: (f) => {
      b.sndm.Enable(!f);
    },
    // Mouse Input — web no longer forces it (PR #124). Write-through to OptionsState (like
    // difficulty/scroll); save() persists kfPrefMouseMode, the next game's ApplyPreferences
    // reads it (and on a touch device does NOT re-force it on). No live game here → no SetUIType.
    mouseMode: () => b.options.fMouseMode,
    setMouseMode: (f) => {
      b.options.fMouseMode = f;
    },
    gameSpeedNotch: notch,
    gameSpeedNotchMax: () => gatGameSpeeds.length - 1,
    // the canvas DEFAULT speed (gameoptions.ts:506): tGameSpeed = trunc(kcmsUpdate/20)
    defaultGameSpeedNotch: () => {
      const i = gatGameSpeeds.indexOf(Math.trunc(kcmsUpdate / 20));
      return i < 0 ? 7 : i;
    },
    setGameSpeedNotch: (i) => {
      tGameSpeed = gatGameSpeeds[Math.max(0, Math.min(gatGameSpeeds.length - 1, Math.round(i)))];
    },
    gameSpeedLabel: () => GetSpeedMultiplierString(tGameSpeed),
    scrollSpeed: () => b.options.nScrollSpeed,
    setScrollSpeed: (v) => {
      b.options.nScrollSpeed = clampScroll(v);
    },
    scrollSpeedLabel: () => formatSpeedX(b.options.nScrollSpeed),
    save: () => {
      SavePreferences(b.prefs, b.prefStore, b.sndm, b.options, tGameSpeed);
    },
  };
}
